import { useCallback, useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import {
  STARTER_CATEGORIES,
  type StarterCategory,
} from '@/versions/v1/shared/welcome-starters';

export function StarterCategories({ onPrefill }: { onPrefill: (text: string) => void }) {
  const [openId, setOpenId] = useState<string | null>(null);

  const handlePromptClick = useCallback(
    (prefill: string) => {
      onPrefill(prefill);
      setOpenId(null);
    },
    [onPrefill],
  );

  const open = openId
    ? STARTER_CATEGORIES.find((c) => c.id === openId) ?? null
    : null;

  if (open) {
    return (
      <StarterPanel
        category={open}
        onPromptClick={handlePromptClick}
        onClose={() => setOpenId(null)}
      />
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5">
      {STARTER_CATEGORIES.map((c, i) => (
        <button
          key={c.id}
          type="button"
          onClick={() => setOpenId(c.id)}
          style={{ animationDelay: `${i * 60}ms` }}
          className="group inline-flex items-center gap-2 rounded-full border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-3.5 py-1.5 text-sm text-zinc-600 opacity-0 backdrop-blur-md transition-all duration-200 [animation:chip-lift_360ms_cubic-bezier(0.22,0.61,0.36,1)_both] hover:-translate-y-px hover:border-[var(--glass-border-strong)] hover:bg-[var(--glass-surface)] hover:text-zinc-900 hover:shadow-sm active:translate-y-0 dark:text-zinc-300 dark:hover:text-zinc-100"
          aria-haspopup="true"
          aria-expanded={false}
        >
          <c.icon className="h-3.5 w-3.5 text-zinc-400 transition-colors group-hover:text-accent-500" />
          <span className="font-normal">{c.label}</span>
        </button>
      ))}
    </div>
  );
}

function StarterPanel({
  category,
  onPromptClick,
  onClose,
}: {
  category: StarterCategory;
  onPromptClick: (prefill: string) => void;
  onClose: () => void;
}) {
  const Icon = category.icon;
  return (
    <div
      className="mx-auto w-full max-w-[var(--thread-max-width)] overflow-hidden rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] backdrop-blur-md [animation:chip-lift_240ms_cubic-bezier(0.22,0.61,0.36,1)_both]"
      role="region"
      aria-label={`${category.label} starter prompts`}
    >
      <div className="flex items-center justify-between gap-3 border-b border-[var(--glass-border)] px-5 py-3">
        <div className="flex items-center gap-2 text-sm text-muted">
          <Icon className="h-4 w-4" />
          <span className="font-medium">{category.label}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close starter prompts"
          className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-[var(--glass-surface)] hover:text-zinc-700 dark:text-zinc-500 dark:hover:text-zinc-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="flex flex-col">
        {category.prompts.map((p, i) => (
          <li key={p.label}>
            <button
              type="button"
              onClick={() => onPromptClick(p.prefill)}
              className={`group flex w-full items-center justify-between gap-3 px-5 py-3 text-left text-sm text-zinc-700 transition-colors hover:bg-[var(--glass-surface)] hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100 ${i > 0 ? 'border-t border-[var(--glass-border)]' : ''}`}
            >
              <span className="flex-1 truncate">{p.label}</span>
              <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-zinc-300 opacity-0 transition-opacity group-hover:opacity-100 dark:text-zinc-600" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
